import clsx from "clsx";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  MdHowToReg,
  MdOutlineFingerprint,
  MdOutlineNavigateNext,
} from "react-icons/md";
import TextInput from "../../components/TextInput/TextInput";
import { useCallback, useEffect, useRef, useState } from "react";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import axios from "axios";
import {
  API_ENDPOINT_LOGIN,
  APP_LOCAL_STORAGE_ACCOUNTS,
  APP_LOCAL_STORAGE_USER_DATA,
  APP_TOKEN_KEY,
  APP_USER_ID_KEY,
  ICON_SIZES,
  NOTIFICATIONS_TABS,
  REPORTS_TABS,
  TRANSACTIONS_TABS,
} from "../../helpers/constants";
import ApiClient from "../../helpers/api-client";
import userPreferences from "../../store/userPreferences";
import { useTranslation } from "react-i18next";
import { showNotification } from "../../helpers/utils";
import LoginFlowLayout from "../../layouts/LoginFlow/LoginFlowLayout";
import useMainFilter from "../../hooks/useMainFilter";
import { useQueryClient } from "@tanstack/react-query";
import LocalStorageUsers from "../../components/LocalStorageUsers/LocalStorageUsers";
import useAuth from "../../hooks/useAuth";
import useMobile from "../../hooks/useMobile";
import { EButtonKind } from "src/components/Button/ButtonTypes";
// import useAccountLocalStorage from "../../hooks/useAccountLocalStorage";

const LOGIN_FORM_ID = "login-form";

const LoginPage: React.FC = () => {
  const { state: routeState } = useLocation();
  const { isMobile } = useMobile();
  const { t } = useTranslation();
  const alreadyHasToken = !!localStorage?.getItem?.(APP_TOKEN_KEY);
  const passwordRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const [isLoadingPage, setIsLoadingPage] = useState<boolean>(true);
  const { loginWithPassKey } = useAuth();
  // const [inpusReadOnly, setInputsReadOnly] = useState<boolean>(true);
  // const { accountLocalStoragePreferences } = useAccountLocalStorage();

  const [email, setEmail] = useState<string>(routeState?.email || "");
  const [password, setPassword] = useState<string>(routeState?.password || "");
  const [isCheckingAccess, setIsCheckingAccess] = useState<boolean>(false);
  const accounts = localStorage.getItem(APP_LOCAL_STORAGE_ACCOUNTS) || "[]";
  const { setMainFilter } = useMainFilter();
  const {
    setRightPanelIsVisible,
    setTransactionsActiveTab,
    setNotificationsActiveTab,
    setReportsActiveTab,
    setSelectedYear,
  } = userPreferences();

  const navigate = useNavigate();

  const redireccionar = useCallback(async () => {
    if (alreadyHasToken) {
      axios.defaults.headers.common["Expenser-User-Id"] =
        localStorage?.getItem?.(APP_USER_ID_KEY);
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${localStorage?.getItem?.(APP_TOKEN_KEY)}`;
      const userId = localStorage?.getItem(APP_USER_ID_KEY);
      const accountSettings = localStorage?.getItem(`${userId}-account`);
      let lastAccount = "";
      if (accountSettings) {
        const parsedSettings = JSON.parse(accountSettings);
        lastAccount = parsedSettings.lastAccount;
        setSelectedYear(parsedSettings.lastYear);
        setMainFilter({ account: lastAccount });
      }

      queryClient.clear();

      await queryClient.refetchQueries();

      navigate(`/transaction/${lastAccount}`, {
        replace: true,
      });
    } else {
      setIsLoadingPage(false);
    }
  }, [alreadyHasToken, navigate]);

  useEffect(() => {
    redireccionar();
  }, [redireccionar]);

  const updateQuickList = (newValue: any) => {
    const { email, picture, name } = newValue;
    const parsedAccounts = JSON.parse(accounts);

    const exist = parsedAccounts?.find?.(
      (account: any) => account.email === email
    );

    if (!exist) {
      localStorage.setItem(
        APP_LOCAL_STORAGE_ACCOUNTS,
        JSON.stringify([...parsedAccounts, newValue])
      );
    } else {
      const index = parsedAccounts.findIndex(
        (account: any) => account.email === email
      );
      if (index !== -1) {
        parsedAccounts[index].picture = picture;
        parsedAccounts[index].name = name;
        localStorage.setItem(
          APP_LOCAL_STORAGE_ACCOUNTS,
          JSON.stringify(parsedAccounts)
        );
      }
    }
  };

  const queryClient = useQueryClient();

  const loginResult = async (data: any) => {
    const { success = false, result } = data;

    if (success) {
      localStorage.setItem(APP_TOKEN_KEY, result.token);
      localStorage.setItem(APP_USER_ID_KEY, result._id);

      axios.defaults.headers.common["Expenser-User-Id"] = result?._id;
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${result?.token}`;

      updateQuickList({
        email: result.email,
        picture: result.picture,
        name: result.name,
      });
      setTransactionsActiveTab(TRANSACTIONS_TABS.ACTIVE_TRANSACTIONS);
      setReportsActiveTab(REPORTS_TABS.TRANSACTIONS_LAST_SEVEN_DAYS);
      setNotificationsActiveTab(NOTIFICATIONS_TABS.RECURRING_TRANSACTIONS);
      localStorage.setItem(
        APP_LOCAL_STORAGE_USER_DATA,
        JSON.stringify({
          email: result.email,
          picture: result.picture,
          name: result.name,
        })
      );
      setMainFilter({
        dateFrom: "",
        dateTo: "",
        type: "",
        status: [],
        payTo: [],
        account: "",
        searchTerm: "",
        paymentMethod: [],
        tags: [],
      });

      // queryClient.clear();
      // await queryClient.refetchQueries();

      // navigate("/reports", { replace: true });
    } else {
      showNotification({
        kind: "error",
        message: "Correo o contraseña incorrectos",
      });
    }
    setIsCheckingAccess(false);
  };

  const loginUser = async () => {
    setIsCheckingAccess(true);

    return await ApiClient.post(API_ENDPOINT_LOGIN, {
      password,
      email,
    })
      .then(async ({ data }) => {
        await loginResult(data);
      })
      .catch(({ response = {} }) => {
        showNotification({
          message: t("login_page.error_message"),
          kind: "error",
        });
      })
      .finally(() => {
        // setIsCheckingAccess(false);
      });
  };

  return (
    <LoginFlowLayout
      title={isMobile ? "" : t("login_page.title")}
      description={isMobile ? "" : t("login_page.description")}
      isLoading={isLoadingPage}
    >
      <div className={clsx(["flex", "flex-col", "gap-6", "w-full"])}>
        {/* {!isMobile && (
          <LocalStorageUsers
            active=""
            onClick={async (email: string) => {
              setEmail(email);

              // if (PublicKeyCredential || !navigator.credentials) {
              //   const res = await loginWithPassKey({ email });
              //   await loginResult((res as any)?.data || {});
              // } else {
              if (passwordRef?.current) {
                passwordRef.current.value = "";
                passwordRef?.current?.focus?.();
              }
              // }
            }}
          />
        )} */}
        <form
          id={LOGIN_FORM_ID}
          className={clsx([
            "flex",
            "flex-col",
            "gap-6",
            "w-full",
            "mt-16",
            "md:mt-0",
          ])}
          onSubmit={async (e: any) => {
            e?.preventDefault?.();
            await loginUser();
          }}
        >
          <TextInput
            autoComplete="username email webauthn"
            id="email"
            disabled={isCheckingAccess}
            value={email}
            onChange={(e: any) => setEmail(e?.target?.value)}
            label={t("login_page.email_label")}
            type="email"
            placeholder={t("login_page.email_placeholder")}
            required
            ref={emailRef}
            onEnter={() => {}}
            // readOnly={inpusReadOnly}
          />
          <TextInput
            autoComplete="current-password"
            id="password"
            disabled={isCheckingAccess}
            value={password}
            onChange={(e: any) => setPassword(e?.target?.value)}
            type="password"
            required
            placeholder={t("login_page.password_placeholder")}
            label={t("login_page.password_label")}
            ref={passwordRef}
            // readOnly={inpusReadOnly}
          />
          <Link
            to="/password-recovery"
            className={clsx(["text-sm", "underline"])}
          >
            {t("password_recovery_page.title")}
          </Link>
          <div
            className={clsx([
              "pt-4",
              "flex",
              "w-full",
              "justify-between",
              "items-center",
            ])}
          >
            <ButtonGroup
              gap={1}
              buttons={[
                {
                  disabled: isCheckingAccess,
                  type: "button",
                  onClick: () => {
                    navigate("/register", {
                      state: {
                        email,
                        password,
                      },
                    });
                  },
                  text: t("login_page.register_label"),
                  icon: <MdHowToReg size={ICON_SIZES.MD} />,
                  hide: false,
                  kind: EButtonKind.NORMAL,
                },
              ]}
              align="left"
            />

            <ButtonGroup
              gap={1}
              buttons={[
                {
                  hide: [
                    !!PublicKeyCredential,
                    !!navigator?.credentials,
                  ].includes(false),
                  disabled: isCheckingAccess || !email,
                  tooltip: "Usar llave de acceso",
                  icon: <MdOutlineFingerprint size={ICON_SIZES.MD} />,
                  onClick: async () => {
                    try {
                      setIsCheckingAccess(true);
                      const res = await loginWithPassKey({ email });
                    } catch (e) {
                    } finally {
                      setIsCheckingAccess(false);
                    }
                  },
                },
                {
                  disabled: isCheckingAccess,
                  kind: EButtonKind.SAVE,
                  text: isMobile ? "" : t("login_page.login_label"),
                  isLoading: isCheckingAccess,
                  onClick: () => {},
                  icon: <MdOutlineNavigateNext size={ICON_SIZES.MD} />,
                  form: LOGIN_FORM_ID,
                  type: "submit",
                },
              ]}
              align="right"
            />
          </div>
        </form>
      </div>
    </LoginFlowLayout>
  );
};

export default LoginPage;
